import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import {
  IWebsiteBuilderDividerSettings,
  TWebsiteBuilderDividerStyle,
  WebsiteBuilderDividerWeight,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderDivider";
import { Select } from "components/select/index";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  IWebsiteBuilderSelectOption,
  renderWebsiteBuilderSelectOptions,
} from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { CustomColorPickerPopover } from "components/colorpicker";

interface IDividerSettingsProps {
  id: string;
  settings: IWebsiteBuilderDividerSettings;
  closeSettings: () => void;
}

export function DividerSettings(props: IDividerSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderDividerSettings>(() => cloneDeep(settings));

  const weightOptions: IWebsiteBuilderSelectOption<WebsiteBuilderDividerWeight>[] = useMemo(() => {
    return [
      {
        label: t("Thin"),
        value: WebsiteBuilderDividerWeight.THIN,
      },
      {
        label: t("Regular"),
        value: WebsiteBuilderDividerWeight.REGULAR,
      },
      {
        label: t("Thick"),
        value: WebsiteBuilderDividerWeight.THICK,
      },
      {
        label: t("Extra Thick"),
        value: WebsiteBuilderDividerWeight.EXTRA_THICK,
      },
    ];
  }, []);

  const styleOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderDividerStyle>[] = useMemo(() => {
    return [
      {
        label: t("Solid"),
        value: "solid",
      },
      {
        label: t("Dashed"),
        value: "dashed",
      },
      {
        label: t("Dotted"),
        value: "dotted",
      },
    ];
  }, []);

  function dispatchSettings(
    prevSettings: IWebsiteBuilderDividerSettings,
    nextSettings: IWebsiteBuilderDividerSettings,
  ) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "divider",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "divider",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderSettings() {
    return (
      <>
        <Select
          label={t("Weight")}
          onChange={(weight: WebsiteBuilderDividerWeight) =>
            handleUpdateState(prev => ({
              ...prev,
              weight,
            }))
          }
          defaultValue={updatedSettings.weight}
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(weightOptions)}
        </Select>
        <Select
          label={t("Style")}
          onChange={(style: TWebsiteBuilderDividerStyle) =>
            handleUpdateState(prev => ({
              ...prev,
              style,
            }))
          }
          defaultValue={updatedSettings.style}
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(styleOptions)}
        </Select>
        <CustomColorPickerPopover
          title={t("Color")}
          hexColor={updatedSettings.color}
          updateHexColor={color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Divider settings")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}
